import Vue from 'vue';
import Vuex from 'vuex';
import notifications from './notifications';
import eventNotifications from './event-notifications';
import user from './user';
import library from './library';
import fleet from './fleet';
import storage from './storage';
import permissions from './permissions';
import roles from './roles';
Vue.use(Vuex);
import ptCoreClient from 'plant-core-client';
import ptApiClient from 'plant-api-client';
import { ACTION_NOTIFICATIONS_ERROR, STORE_NOTIFICATIONS_NAME } from '@/store_constants/notifications';
import { ACTION_LOGOUT, STORE_USER_NAME } from '@/store_constants/user';

const store = new Vuex.Store({
	plugins: [
		storage({
			storedKeys: ['user', 'library', 'item'],
			destroyOn: ['destroySession', 'destroyItem'],
			watchers: [
				{
					getter: state => state.user.token,
					cb: token => {
						ptCoreClient.token = token;
						ptApiClient.token = token;
					}
				},
				{
					getter: state => state.library.settings.url,
					cb: url => (ptApiClient.baseURL = url)
				}
			]
		})
	],
	modules: {
		eventNotifications,
		notifications,
		user,
		library,
		fleet,
		permissions,
		roles
	},
	state: {
		loading: false,
		loadingMessage: 'Loading',
		buffer: 0,
		loadingTriggered: false,
		mapAxiosResolved: {}
	},
	mutations: {
		setLoading(state, loading) {
			state.loading = loading;
		},
		setLoadingMessage(state, message) {
			state.loadingMessage = message;
		},
		setBuffer(state, value) {
			state.buffer = value;
		},
		setTrigger(state, value) {
			state.loadingTriggered = value;
		},
		updateAxiosResolved(state, { url, flag }) {
			state.mapAxiosResolved[url] = flag;
		}
	},
	actions: {
		activateLoading({ commit }, data) {
			if (data && data.loadingMessage) commit('setLoadingMessage', data.loadingMessage);
			commit('setLoading', true);
		},
		finishLoading({ commit }) {
			commit('setLoadingMessage', 'Loading');
			commit('setBuffer', 0);
			commit('setLoading', false);
			commit('setTrigger', false);
		},
		loadBuffer({ commit }, buffer) {
			commit('setBuffer', buffer);
			if (buffer === 100) store.dispatch('finishLoading');
		},
		triggerLoading({ commit }) {
			commit('setTrigger', true);
		},
		setAxiosResolved({ commit }, { url, flag }) {
			commit('updateAxiosResolved', { url, flag });
		}
	}
});

ptApiClient.interceptors.request.use(function (config) {
	store.dispatch('setAxiosResolved', { url: config.url, flag: false });
	if (config.method === 'post') {
		setTimeout(() => {
			if (!store.state.mapAxiosResolved[config.url]) {
				store.dispatch('activateLoading', { loadingMessage: 'Processing request' });
			}
		}, 2000);
	}
	config.onUploadProgress = progressEvent => {
		const { loaded, total } = progressEvent;
		// Show the upload progress bar if the request has a length that can be calculated
		// and it is larger than 25000000 bytes (25MB)
		// https://developer.mozilla.org/en-US/docs/Web/API/ProgressEvent/lengthComputable
		if (progressEvent.lengthComputable && progressEvent.total > 25000000) {
			store.dispatch('triggerLoading');
			store.dispatch('activateLoading', { loadingMessage: 'Uploading' });
		}
		store.dispatch('loadBuffer', Math.round((loaded * 100) / total));
	};
	return config;
});

function onRejected(error) {
	if (error.response && error.response.status === 401) {
		store.dispatch(`${STORE_NOTIFICATIONS_NAME}/${ACTION_NOTIFICATIONS_ERROR}`, {
			message: 'Authentication is required to perform this action. Please sign in and try again.'
		});
		store.dispatch(`${STORE_USER_NAME}/${ACTION_LOGOUT}`);
	} else if (error.response && error.response.status === 403) {
		store.dispatch(`${STORE_NOTIFICATIONS_NAME}/${ACTION_NOTIFICATIONS_ERROR}`, {
			message:
				'You are not authorized to perform this action. Please contact your administrator if you believe this is an error.'
		});
	} else if (error.response && error.response.status >= 500) {
		store.dispatch(`${STORE_NOTIFICATIONS_NAME}/${ACTION_NOTIFICATIONS_ERROR}`, {
			message: 'Something went wrong on our side. Please try again later.'
		});
	}
	return Promise.reject(error);
}

ptApiClient.interceptors.response.use(
	function (response) {
		store.dispatch('setAxiosResolved', { url: response.config.url, flag: true });
		store.dispatch('finishLoading');
		return response;
	},
	function (error) {
		store.dispatch('setAxiosResolved', { url: error.config.url, flag: true });
		store.dispatch('finishLoading');
		return onRejected(error);
	}
);

ptCoreClient.interceptors.response.use(function (response) {
	return response;
}, onRejected);

export default store;
